import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, User as UserIcon, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ImageCarousel } from '@/components/ImageCarousel';
import { ImageItem } from '@/lib/image-utils';
import { sanitizeHtml } from '@/lib/html-utils';
import { RoleBadgeList } from '@/components/RoleBadge';
import { formatDistanceToNow } from 'date-fns';
import { cs, enUS } from 'date-fns/locale';

interface StepCompletion {
  step_id: string;
  operator_id: string;
  completed_at: string;
  operator?: {
    full_name: string;
  } | null;
}

interface InstructionStep {
  id: string;
  step_number: number;
  title: string;
  description: string;
  image_url: ImageItem[];
  is_quality_check: boolean;
  evidence_required: boolean;
  parent_step_id?: string | null;
  part_number?: string | null;
  tools_equipment?: string | null;
  ppe?: string | null;
  tools_equipment_image_url?: ImageItem[];
  required_roles?: string[] | null;
}

interface WorkflowContentProps {
  currentStep: InstructionStep;
  isStepCompleted: boolean;
  stepCompletion?: StepCompletion;
  currentUserId?: string;
}

export const WorkflowContent = ({
  currentStep,
  isStepCompleted,
  stepCompletion,
  currentUserId,
}: WorkflowContentProps) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language.startsWith('cs') ? cs : enUS;

  const formatRelativeTime = (date: string | null) => {
    if (!date) return '';
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: dateLocale });
    } catch {
      return '';
    }
  };

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-2xl flex items-center gap-2">
            {isStepCompleted && (
              <CheckCircle2 className="h-6 w-6 text-success" />
            )}
            {currentStep.title}
          </CardTitle>
          <div className="flex items-center gap-2">
            {currentStep.is_quality_check && (
              <Badge variant="secondary" className="bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100">
                {t('workflow.qualityCheck')}
              </Badge>
            )}
          </div>
        </div>

        {/* Required Roles Display */}
        {currentStep.required_roles && currentStep.required_roles.length > 0 && (
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {t('workflow.requiredRoles')}:
              </span>
              <RoleBadgeList roles={currentStep.required_roles} size="sm" />
            </div>
          </div>
        )}

        {/* Step Completion Info */}
        {isStepCompleted && stepCompletion && (
          <div className="mt-3 p-3 bg-success/10 border border-success/20 rounded-lg">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-4 w-4 text-success" />
              <div className="flex-1">
                <div className="text-sm font-medium text-success">{t('workflow.stepCompleted')}</div>
                <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                  {stepCompletion.operator && (
                    <div className="flex items-center gap-1">
                      <UserIcon className="h-3 w-3" />
                      <span>
                        {stepCompletion.operator_id === currentUserId
                          ? t('operator.you')
                          : stepCompletion.operator.full_name}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>{formatRelativeTime(stepCompletion.completed_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Step Images */}
        {currentStep.image_url.length > 0 && (
          <ImageCarousel images={currentStep.image_url} alt={currentStep.title} />
        )}

        {/* Description */}
        <div 
          className="prose prose-sm max-w-none dark:prose-invert"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentStep.description) }}
        />

        {/* Part Number */}
        {currentStep.part_number && (
          <div className="p-4 bg-muted rounded-lg border border-border">
            <span className="text-sm font-medium">{t('workflow.partNumber')}: </span>
            <span className="font-mono text-lg font-bold">{currentStep.part_number}</span>
          </div>
        )}

        {/* Tools & Equipment */}
        {currentStep.tools_equipment && (
          <div className="p-4 bg-muted rounded-lg border border-border">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium">{t('workflow.toolsEquipment')}: </span>
            </div>
            <span className="text-sm">{currentStep.tools_equipment}</span>
            {currentStep.tools_equipment_image_url && currentStep.tools_equipment_image_url.length > 0 && (
              <div className="mt-3">
                <ImageCarousel images={currentStep.tools_equipment_image_url} alt={t('workflow.toolsEquipmentAlt')} />
              </div>
            )}
          </div>
        )}

        {/* PPE */}
        {currentStep.ppe && (
          <div className="p-4 bg-warning/10 border border-warning/30 rounded-lg">
            <span className="text-sm font-bold text-warning-foreground">{t('workflow.ppeRequired')}: </span>
            <span className="text-sm">{currentStep.ppe}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
